import { useCallback, useEffect, useState, Fragment } from "react";
import { ChevronDown, ChevronRight, TriangleAlert, Link2, ShieldCheck, Trash2, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/Bits";
import { RollReturnsHQ } from "@/components/RollReturnsHQ";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";

const PRODUCTS = ["Veloskin PPF", "Velotint Sunfilm"];
const STATUS = ["available", "dispatched", "received", "consumed", "void"];

const Stat = ({ label, value, tone = "text-brand-ink", testid }) => (
  <div className="rounded-lg border border-brand-line bg-white px-4 py-3" data-testid={testid}>
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className={`font-display text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

export default function SerialTrace() {
  const { user } = useAuth();
  const canDelete = ["network_admin", "accountant"].includes(user.role);
  const canViewTransfers = ["network_admin", "accountant", "sales_manager_products"].includes(user.role);
  const [tab, setTab] = useState("rolls");
  const [transfers, setTransfers] = useState([]);
  const [tStudio, setTStudio] = useState("all");
  const [tLoading, setTLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [fProduct, setFProduct] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [open, setOpen] = useState({});
  const [linkFor, setLinkFor] = useState(null);
  const [linkForm, setLinkForm] = useState({ job_id: "", vehicle: "", detailer: "", sqft: "", note: "" });
  const [selected, setSelected] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [historyById, setHistoryById] = useState({});
  const [studios, setStudios] = useState([]);
  const [detailers, setDetailers] = useState([]);
  const [xfer, setXfer] = useState(null);
  const [xferDest, setXferDest] = useState("");
  const [xferNote, setXferNote] = useState("");
  const [xferCharge, setXferCharge] = useState(false);
  const [xferBusy, setXferBusy] = useState(false);

  useEffect(() => {
    if (canViewTransfers)
      api.get("/roll-serials/transfer-destinations")
        .then(({ data }) => { setStudios(data.studios || []); setDetailers(data.detailers || []); })
        .catch(() => {});
  }, [canViewTransfers]);

  const loadTransfers = useCallback(() => {
    if (!canViewTransfers) return;
    setTLoading(true);
    const q = tStudio !== "all" ? `?studio_id=${tStudio}` : "";
    api
      .get(`/roll-serials/transfers${q}`)
      .then(({ data }) => setTransfers(data || []))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setTLoading(false));
  }, [canViewTransfers, tStudio]);

  useEffect(() => { if (tab === "transfers") loadTransfers(); }, [tab, loadTransfers]);

  const doTransfer = async () => {
    if (!xferDest) return toast.error("Choose a destination");
    const [destType, destId] = xferDest.split(":");
    setXferBusy(true);
    try {
      const payload = {
        dest_type: destType, note: xferNote, charge: xferCharge,
        ...(destType === "detailer" ? { detailer_id: destId } : { studio_id: destId }),
      };
      const { data } = await api.post(`/roll-serials/${xfer.id}/transfer`, payload);
      toast.success(
        data?.charge_order_id
          ? `Roll ${xfer.serial} transferred · ₹${Math.round(data.charge_amount)} billed`
          : `Roll ${xfer.serial} transferred`
      );
      setXfer(null); setXferDest(""); setXferNote(""); setXferCharge(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setXferBusy(false);
    }
  };

  const toggleRow = (r) => {
    setOpen((o) => ({ ...o, [r.id]: !o[r.id] }));
    if (!historyById[r.id]) {
      api
        .get(`/roll-serials/${r.id}/history`)
        .then(({ data }) => setHistoryById((h) => ({ ...h, [r.id]: data.events || [] })))
        .catch(() => setHistoryById((h) => ({ ...h, [r.id]: [] })));
    }
  };

  const deletable = (r) =>
    !["dispatched", "received"].includes(r.status) &&
    (r.consumed_sqft || 0) <= 0 &&
    !(r.b2b_links || []).length &&
    !(r.usage || []).length;

  const load = useCallback(() => {
    const p = [];
    if (fProduct !== "all") p.push(`product_type=${encodeURIComponent(fProduct)}`);
    if (fStatus !== "all") p.push(`status=${fStatus}`);
    if (search.trim()) p.push(`search=${encodeURIComponent(search.trim())}`);
    if (flaggedOnly) p.push("flagged_only=true");
    api
      .get(`/reports/serial-traceability${p.length ? "?" + p.join("&") : ""}`)
      .then(({ data }) => {
        setRows(data.rows || []);
        setCount(data.count || 0);
        setFlaggedCount(data.flagged || 0);
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [fProduct, fStatus, search, flaggedOnly]);

  useEffect(() => { load(); }, [load]);

  const submitLink = async () => {
    if (!linkForm.job_id.trim() && !linkForm.vehicle.trim() && !linkForm.detailer.trim())
      return toast.error("Enter a job ID, vehicle or detailer reference");
    try {
      await api.post(`/roll-serials/${linkFor.id}/link-job`, {
        job_id: linkForm.job_id.trim() || null,
        vehicle: linkForm.vehicle.trim(),
        detailer: linkForm.detailer.trim(),
        sqft: linkForm.sqft ? Number(linkForm.sqft) : null,
        note: linkForm.note.trim(),
      });
      toast.success("Serial linked to job");
      setLinkFor(null);
      setLinkForm({ job_id: "", vehicle: "", detailer: "", sqft: "", note: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectableRows = rows.filter(deletable);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected[r.id]);
  const toggleAll = () => {
    if (allSelected) return setSelected({});
    const next = {};
    selectableRows.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/roll-serials/${confirmDel.id}`);
      toast.success(`Serial ${confirmDel.serial} deleted`);
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doBulkDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/roll-serials/bulk-delete", { ids: selectedIds });
      if (data.deleted_count) toast.success(`${data.deleted_count} serial(s) deleted`);
      if ((data.skipped || []).length) {
        toast.warning(
          `Skipped ${data.skipped.length}: ` +
            data.skipped.map((s) => `${s.serial} (${s.reason})`).join(", "),
          { duration: 8000 }
        );
      }
      setConfirmBulk(false);
      setSelected({});
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="serial-trace-page">
      <PageHead
        title="Serial Traceability"
        subtitle="HQ view of every roll — where its sqft went, job by job. Rolls that show consumption with no job reference are flagged red."
      />

      {canViewTransfers && (
        <div className="mb-5 flex gap-1 border-b border-brand-line" data-testid="st-tabs">
          <button
            data-testid="st-tab-rolls"
            onClick={() => setTab("rolls")}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === "rolls" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-brand-ink"
            }`}
          >
            Roll traceability
          </button>
          <button
            data-testid="st-tab-transfers"
            onClick={() => setTab("transfers")}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === "transfers" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-brand-ink"
            }`}
          >
            Transfers
          </button>
        </div>
      )}

      {tab === "transfers" ? (
        <div data-testid="st-transfers-view">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Label className="text-xs text-slate-500">Holder</Label>
            <Select value={tStudio} onValueChange={setTStudio}>
              <SelectTrigger className="h-10 w-64" data-testid="st-transfers-studio"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All holders (network-wide)</SelectItem>
                <SelectGroup>
                  <SelectLabel>Network studios</SelectLabel>
                  {studios.map((st) => (
                    <SelectItem key={st.id} value={st.id}>{st.name}{st.city ? ` · ${st.city}` : ""}</SelectItem>
                  ))}
                </SelectGroup>
                {detailers.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Non-accredited detailers</SelectLabel>
                    {detailers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}{d.city ? ` · ${d.city}` : ""}</SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-10" data-testid="st-transfers-refresh" onClick={loadTransfers}>
              Refresh
            </Button>
            <span className="ml-auto text-xs text-slate-500" data-testid="st-transfers-count">
              {transfers.length} transfer{transfers.length === 1 ? "" : "s"}
            </span>
          </div>
          <Card className="border-brand-line shadow-none">
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full text-sm" data-testid="st-transfers-table">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="p-2.5">When</th>
                    <th className="p-2.5">Serial</th>
                    <th className="p-2.5">Product</th>
                    <th className="p-2.5 text-right">Sqft moved</th>
                    <th className="p-2.5">Movement</th>
                    <th className="p-2.5">By</th>
                    <th className="p-2.5">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {tLoading && (
                    <tr><td colSpan={7} className="p-6 text-center text-slate-400">Loading…</td></tr>
                  )}
                  {!tLoading && transfers.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-slate-400" data-testid="st-transfers-empty">No transfers recorded{tStudio !== "all" ? " for this studio" : ""} yet.</td></tr>
                  )}
                  {!tLoading && transfers.map((t) => (
                    <tr key={t.id} className="border-t border-brand-line" data-testid={`st-transfer-row-${t.serial}`}>
                      <td className="p-2.5 whitespace-nowrap text-slate-600">{(t.at || "").replace("T", " ").slice(0, 16)}</td>
                      <td className="p-2.5 font-mono">{t.serial}</td>
                      <td className="p-2.5">{t.product_type}</td>
                      <td className="p-2.5 text-right font-medium">{Math.round(t.sqft_moved || 0)}</td>
                      <td className="p-2.5">
                        {t.direction && (
                          <Badge
                            className={`mr-2 border-0 ${t.direction === "out" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}
                          >
                            {t.direction === "out" ? "Sent out" : "Received"}
                          </Badge>
                        )}
                        <span className="text-xs text-slate-600">
                          {t.from_studio_name || "—"} <ArrowLeftRight className="mx-1 inline h-3 w-3 text-slate-400" /> {t.to_studio_name || "—"}
                        </span>
                      </td>
                      <td className="p-2.5 text-xs text-slate-600">{t.actor_name || "—"}</td>
                      <td className="p-2.5 text-xs text-slate-500">
                        {t.charged ? (
                          <Badge className="mr-1.5 border-0 bg-indigo-100 text-indigo-800">Charged ₹{Math.round(t.charge_amount || 0)}</Badge>
                        ) : (
                          <Badge className="mr-1.5 border-0 bg-slate-100 text-slate-600">Free</Badge>
                        )}
                        {t.note || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      ) : (
      <>
      <RollReturnsHQ />

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Stat label="Rolls in view" value={count} testid="st-count" />
        <Stat
          label="Red-flag rolls"
          value={flaggedCount}
          tone={flaggedCount ? "text-rose-600" : "text-emerald-600"}
          testid="st-flagged"
        />
        <Stat
          label="Consumed sqft (in view)"
          value={Math.round(rows.reduce((s, r) => s + (r.consumed_sqft || 0), 0))}
          testid="st-consumed"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          className="h-10 w-full sm:w-64"
          placeholder="Search serial number"
          data-testid="st-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={fProduct} onValueChange={setFProduct}>
          <SelectTrigger className="h-10 w-44" data-testid="st-filter-product"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-10 w-40" data-testid="st-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant={flaggedOnly ? "default" : "outline"}
          size="sm"
          className={flaggedOnly ? "bg-rose-600 hover:bg-rose-700" : ""}
          data-testid="st-flagged-toggle"
          onClick={() => setFlaggedOnly((v) => !v)}
        >
          <TriangleAlert className="mr-1.5 h-4 w-4" /> Red flags only
        </Button>
        {canDelete && selectedIds.length > 0 && (
          <Button
            size="sm"
            className="ml-auto bg-rose-600 hover:bg-rose-700"
            data-testid="st-bulk-delete-btn"
            onClick={() => setConfirmBulk(true)}
          >
            <Trash2 className="mr-1.5 h-4 w-4" /> Delete {selectedIds.length} selected
          </Button>
        )}
      </div>

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="st-table">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                {canDelete && (
                  <th className="p-2.5">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      data-testid="st-select-all"
                      aria-label="Select all deletable serials"
                    />
                  </th>
                )}
                <th className="p-2.5"></th>
                <th className="p-2.5">Serial</th>
                <th className="p-2.5">Product</th>
                <th className="p-2.5">Studio / Detailer</th>
                <th className="p-2.5 text-right">Capacity</th>
                <th className="p-2.5 text-right">Consumed</th>
                <th className="p-2.5 text-right">Balance</th>
                <th className="p-2.5">Jobs</th>
                <th className="p-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={canDelete ? 10 : 9} className="p-6 text-center text-slate-400">No rolls match.</td></tr>
              )}
              {rows.map((r) => {
                const jobCount = (r.usage || []).length + (r.b2b_links || []).length;
                const isOpen = open[r.id];
                return (
                  <Fragment key={r.id}>
                    <tr
                      className={`border-t border-brand-line ${r.red_flag ? "bg-rose-50/60" : ""}`}
                      data-testid={`st-row-${r.serial}`}
                    >
                      {canDelete && (
                        <td className="p-2.5">
                          <Checkbox
                            checked={!!selected[r.id]}
                            disabled={!deletable(r)}
                            onCheckedChange={(v) => setSelected((s) => ({ ...s, [r.id]: !!v }))}
                            data-testid={`st-check-${r.serial}`}
                            aria-label={`Select ${r.serial}`}
                          />
                        </td>
                      )}
                      <td className="p-2.5">
                        <button
                          data-testid={`st-expand-${r.serial}`}
                          onClick={() => toggleRow(r)}
                          className="text-slate-400 hover:text-brand"
                        >
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="p-2.5 font-mono">
                        {r.serial}
                        {r.red_flag && (
                          <Badge className="ml-2 border-0 bg-rose-600 text-white" data-testid={`st-flag-${r.serial}`}>
                            <TriangleAlert className="mr-1 h-3 w-3" /> No job ref
                          </Badge>
                        )}
                      </td>
                      <td className="p-2.5">
                        <div>{r.product_type}</div>
                        <span
                          data-testid={`st-source-${r.serial}`}
                          className={`mt-0.5 inline-block rounded-full px-1.5 py-0.5 text-[10px] ${
                            r.origin === "opening_stock" || r.origin === "stock_backfill"
                              ? "bg-sky-50 text-sky-700"
                              : "bg-violet-50 text-violet-700"
                          }`}
                        >
                          {r.origin === "opening_stock" || r.origin === "stock_backfill"
                            ? "Opening stock"
                            : "Dispatch"}
                        </span>
                      </td>
                      <td className="p-2.5 text-xs text-slate-600">
                        {r.recipient}
                        {r.holder_type === "detailer"
                          ? <Badge className="ml-1.5 border-0 bg-amber-50 text-amber-700">Non-accredited</Badge>
                          : (r.is_b2b && <Badge className="ml-1.5 border-0 bg-violet-50 text-violet-700">B2B</Badge>)}
                      </td>
                      <td className="p-2.5 text-right">{r.total_capacity}</td>
                      <td className="p-2.5 text-right font-medium">{r.consumed_sqft}</td>
                      <td className="p-2.5 text-right">{r.balance_sqft}</td>
                      <td className="p-2.5">
                        <button
                          className="text-brand hover:underline"
                          data-testid={`st-jobs-${r.serial}`}
                          onClick={() => toggleRow(r)}
                        >
                          {jobCount} job{jobCount === 1 ? "" : "s"}
                        </button>
                      </td>
                      <td className="p-2.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {r.is_b2b && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              data-testid={`st-link-${r.serial}`}
                              onClick={() => { setLinkFor(r); setLinkForm({ job_id: "", vehicle: "", detailer: "", sqft: "", note: "" }); }}
                            >
                              <Link2 className="mr-1 h-3.5 w-3.5" /> Link job
                            </Button>
                          )}
                          {canDelete && (r.studio_id || r.holder_type === "detailer" || r.is_b2b)
                            && ["received", "dispatched"].includes(r.status)
                            && (r.balance_sqft || 0) > 0.01 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              data-testid={`st-transfer-${r.serial}`}
                              onClick={() => { setXfer(r); setXferDest(""); setXferNote(""); setXferCharge(false); }}
                            >
                              <ArrowLeftRight className="mr-1 h-3.5 w-3.5" /> Transfer
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                              disabled={!deletable(r)}
                              title={deletable(r) ? "Delete serial" : "Cannot delete — linked to a job or dispatched"}
                              data-testid={`st-delete-${r.serial}`}
                              onClick={() => setConfirmDel(r)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-brand-line bg-slate-50/40" data-testid={`st-drill-${r.serial}`}>
                        <td colSpan={canDelete ? 10 : 9} className="p-3">
                          {jobCount === 0 ? (
                            <p className={`text-xs ${r.red_flag ? "font-semibold text-rose-700" : "text-slate-500"}`}>
                              {r.red_flag
                                ? `⚠ ${r.consumed_sqft} sqft consumed but NO job reference recorded — investigate.`
                                : "No consumption recorded against this roll yet."}
                            </p>
                          ) : (
                            <div className="space-y-1.5">
                              {(r.usage || []).map((u, i) => (
                                <div key={`u${i}`} className="flex flex-wrap items-center justify-between gap-2 rounded bg-white px-3 py-1.5 text-xs">
                                  <span className="font-medium text-brand-ink">
                                    {u.job_name || u.customer_name || "Job"}
                                    {u.studio_name ? <span className="text-slate-400"> · {u.studio_name}</span> : ""}
                                  </span>
                                  <span className="text-slate-500">{u.date} · {u.sqft} sqft · {u.ctx}</span>
                                </div>
                              ))}
                              {(r.b2b_links || []).map((b, i) => (
                                <div key={`b${i}`} className="flex flex-wrap items-center justify-between gap-2 rounded bg-violet-50 px-3 py-1.5 text-xs">
                                  <span className="font-medium text-violet-800">
                                    B2B link: {b.job_name || b.vehicle || b.detailer || "reference"}
                                    {b.detailer ? <span className="text-violet-500"> · {b.detailer}</span> : ""}
                                  </span>
                                  <span className="text-violet-500">
                                    {b.date}{b.sqft ? ` · ${b.sqft} sqft` : ""}{b.note ? ` · ${b.note}` : ""}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="mt-3" data-testid={`st-history-${r.serial}`}>
                            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                              Full lifecycle
                            </p>
                            {(historyById[r.id] || []).length === 0 ? (
                              <p className="text-xs text-slate-400">
                                No lifecycle events recorded yet (events are logged from now on:
                                created → dispatched → received → consumed → returned → write-off).
                              </p>
                            ) : (
                              <ol className="space-y-1">
                                {(historyById[r.id] || []).map((e) => (
                                  <li
                                    key={e.id}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded bg-white px-3 py-1.5 text-xs"
                                  >
                                    <span className="font-medium capitalize text-brand-ink">
                                      {String(e.action).replace(/_/g, " ")}
                                      {e.studio_name ? <span className="text-slate-400"> · {e.studio_name}</span> : ""}
                                      {e.job_name ? <span className="text-slate-400"> · {e.job_name}</span> : ""}
                                    </span>
                                    <span className="text-slate-500">
                                      {e.date}
                                      {e.sqft_delta ? ` · ${e.sqft_delta > 0 ? "+" : ""}${e.sqft_delta} sqft` : ""}
                                      {` · bal ${e.balance_after} sqft`}
                                      {e.actor_name ? ` · ${e.actor_name}` : ""}
                                    </span>
                                  </li>
                                ))}
                              </ol>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </>
      )}

      <Dialog open={!!linkFor} onOpenChange={(o) => !o && setLinkFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="st-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand" /> Link {linkFor?.serial} to a job
            </DialogTitle>
            <DialogDescription>
              For non-accredited detailers there is no studio stock flow — record which job / vehicle
              this dispatched roll was used on.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Job ID (optional)</Label>
              <Input className="mt-1" data-testid="st-link-jobid" value={linkForm.job_id}
                onChange={(e) => setLinkForm((f) => ({ ...f, job_id: e.target.value }))}
                placeholder="Paste the job ID if it exists in the system" />
            </div>
            <div>
              <Label className="text-xs">Vehicle</Label>
              <Input className="mt-1" data-testid="st-link-vehicle" value={linkForm.vehicle}
                onChange={(e) => setLinkForm((f) => ({ ...f, vehicle: e.target.value }))}
                placeholder="e.g. BMW X5 · KL07 AB 1234" />
            </div>
            <div>
              <Label className="text-xs">Detailer</Label>
              <Input className="mt-1" data-testid="st-link-detailer" value={linkForm.detailer}
                onChange={(e) => setLinkForm((f) => ({ ...f, detailer: e.target.value }))}
                placeholder="Non-accredited detailer name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Sqft used (optional)</Label>
                <Input className="mt-1" inputMode="decimal" data-testid="st-link-sqft" value={linkForm.sqft}
                  onChange={(e) => setLinkForm((f) => ({ ...f, sqft: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Input className="mt-1" data-testid="st-link-note" value={linkForm.note}
                  onChange={(e) => setLinkForm((f) => ({ ...f, note: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkFor(null)}>Cancel</Button>
            <Button data-testid="st-link-submit" onClick={submitLink}>Link serial</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="st-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete serial?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete serial {confirmDel?.serial}? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" disabled={busy} data-testid="st-delete-confirm" onClick={doDelete}>
              {busy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmBulk} onOpenChange={(o) => !o && setConfirmBulk(false)}>
        <DialogContent className="sm:max-w-md" data-testid="st-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {selectedIds.length} serial numbers?</DialogTitle>
            <DialogDescription>
              Delete {selectedIds.length} serial numbers? This cannot be undone. Any serial linked to
              a job or dispatched material will be skipped and listed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulk(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" disabled={busy} data-testid="st-bulk-delete-confirm" onClick={doBulkDelete}>
              {busy ? "Deleting…" : `Delete ${selectedIds.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!xfer} onOpenChange={(o) => !o && setXfer(null)}>
        <DialogContent data-testid="st-transfer-dialog">
          <DialogHeader>
            <DialogTitle>Transfer roll to another studio or detailer</DialogTitle>
            <DialogDescription>
              The serial number and its remaining sqft move with the roll and stay traceable at the new holder.
            </DialogDescription>
          </DialogHeader>
          {xfer && (
            <div className="space-y-4 text-sm">
              <div className="rounded-md border border-brand-line bg-slate-50 p-3">
                <p className="font-mono font-semibold">{xfer.serial}</p>
                <p className="text-slate-500">{xfer.product_type} · {Math.round(xfer.balance_sqft)} sqft left</p>
                <p className="mt-1 text-slate-500">Currently at: <span className="font-medium text-slate-700">{xfer.recipient || "—"}</span></p>
              </div>
              <div>
                <Label>Transfer to</Label>
                <Select value={xferDest} onValueChange={setXferDest}>
                  <SelectTrigger className="mt-1.5 min-h-[40px]" data-testid="st-transfer-dest">
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Network studios</SelectLabel>
                      {studios.filter((st) => !(xfer.holder_type !== "detailer" && st.id === xfer.studio_id)).map((st) => (
                        <SelectItem key={`studio:${st.id}`} value={`studio:${st.id}`}>{st.name}{st.city ? ` · ${st.city}` : ""}</SelectItem>
                      ))}
                    </SelectGroup>
                    {detailers.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Non-accredited detailers</SelectLabel>
                        {detailers.filter((d) => !(xfer.holder_type === "detailer" && d.id === xfer.detailer_id)).map((d) => (
                          <SelectItem key={`detailer:${d.id}`} value={`detailer:${d.id}`}>{d.name}{d.city ? ` · ${d.city}` : ""}</SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input className="mt-1.5" data-testid="st-transfer-note" value={xferNote}
                  placeholder="e.g. HQ low on stock" onChange={(e) => setXferNote(e.target.value)} />
              </div>
              <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-brand-line bg-slate-50 p-3" htmlFor="st-transfer-charge">
                <Checkbox
                  id="st-transfer-charge"
                  checked={xferCharge}
                  onCheckedChange={(v) => setXferCharge(!!v)}
                  data-testid="st-transfer-charge"
                  className="mt-0.5"
                />
                <span className="text-sm">
                  <span className="font-medium text-brand-ink">Charge the receiving party</span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Bills the recipient for the {Math.round(xfer.balance_sqft)} sqft at the roll's SKU price
                    (prorated) — a studio gets a material order, a non-accredited detailer a B2B order. The
                    accountant marks it paid. Leave off for a free rebalance.
                  </span>
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setXfer(null)}>Cancel</Button>
            <Button disabled={xferBusy} data-testid="st-transfer-confirm" onClick={doTransfer}>
              {xferBusy ? "Transferring…" : "Transfer roll"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
